
import React from 'react';
import { cn } from '@/lib/utils';
import { Star, ThumbsUp, ThumbsDown, MessageCircle, User, Calendar, MapPin } from 'lucide-react';

interface ReviewCardProps {
  name: string;
  stars: number;
  date: string;
  text: string;
  style?: 'minimal' | 'modern' | 'classic' | 'colorful' | 'compact' | 'elegant';
  className?: string;
  animation?: string;
  isPositive?: boolean;
}

const ReviewCard = ({ 
  name, 
  stars, 
  date, 
  text, 
  style = 'minimal',
  className,
  animation,
  isPositive = true
}: ReviewCardProps) => {
  const renderStars = () => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          size={16}
          className={i < stars ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}
        />
      ));
  };

  const cardStyles = {
    minimal: 'bg-white border border-gray-100 shadow-sm rounded-xl p-4',
    modern: 'glass-card p-4',
    classic: 'bg-white border-2 border-gray-200 rounded-lg p-4 shadow-md',
    colorful: 'bg-gradient-to-r from-pink-50 to-purple-50 border border-purple-100 rounded-xl p-4 shadow-sm',
    compact: 'bg-white border-l-4 border-green-500 border-y border-r shadow-sm rounded-r-lg p-3',
    elegant: 'bg-gradient-to-b from-gray-50 to-white border border-gray-200 rounded-xl p-5 shadow-md'
  };

  const getSentimentIcon = () => {
    if (style === 'colorful') {
      return isPositive ? 
        <ThumbsUp size={18} className="text-green-500" /> :
        <ThumbsDown size={18} className="text-red-500" />;
    }
    if (style === 'compact') {
      return isPositive ? 
        <MessageCircle size={16} className="text-green-600" /> :
        <MessageCircle size={16} className="text-amber-600" />;
    }
    if (style === 'elegant') {
      return <User size={18} className="text-indigo-500" />;
    }
    return null;
  };

  // Handle header layouts based on style
  const renderHeader = () => {
    if (style === 'colorful') {
      return (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
              {name.charAt(0)}
            </div>
            <div>
              <p className="font-semibold text-sm text-purple-800">{name}</p>
              <div className="flex items-center gap-1 text-xs text-purple-600">
                <Calendar size={12} />
                <span>{date}</span>
              </div>
            </div>
          </div>
          {getSentimentIcon()}
        </div>
      );
    } 
    
    if (style === 'compact') {
      return (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            {getSentimentIcon()}
            <p className="font-semibold text-sm text-green-800">{name}</p>
          </div>
          <p className="text-xs text-muted-foreground">{date}</p>
        </div>
      );
    }
    
    if (style === 'elegant') {
      return (
        <div className="flex flex-col items-center mb-3 text-center">
          <div className="w-12 h-12 mb-2 rounded-full bg-indigo-100 flex items-center justify-center">
            {getSentimentIcon()}
          </div>
          <p className="font-medium text-md">{name}</p>
          <p className="text-xs text-muted-foreground mb-2">{date}</p>
          <div className="flex mb-2">{renderStars()}</div>
        </div>
      );
    }
    
    // Default header for minimal, modern, classic
    return (
      <>
        <div className="flex items-center justify-between mb-2">
          <p className="font-semibold text-sm">{name}</p>
          <p className="text-xs text-muted-foreground">{date}</p>
        </div>
        <div className="flex mb-3">{renderStars()}</div>
      </>
    );
  };

  return (
    <div 
      className={cn(
        cardStyles[style],
        animation,
        'transition-all duration-300',
        className
      )}
    >
      {renderHeader()}
      <p className={cn(
        "text-sm leading-relaxed text-balance",
        style === 'colorful' && 'text-purple-900',
        style === 'compact' && 'text-green-900',
        style === 'elegant' && 'text-center text-gray-700'
      )}>
        {text}
      </p>
    </div>
  );
};

export default ReviewCard;
